'use client';

import { useSession } from '@/lib/auth-client';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

// Routes that don't require authentication (accessible by anyone)
const PUBLIC_ROUTES = [
	'/',
	'/login',
	'/signup',
	'/forgot-password',
	'/reset-password',
	'/discover',
	'/terms',
	'/privacy',
	'/about'
];
// Route prefixes that don't require authentication
const PUBLIC_ROUTE_PREFIXES = ['/share/'];
// Routes that authenticated users should be redirected away from
const AUTH_REDIRECT_ROUTES = ['/login', '/signup'];

export function AuthGuard({ children }: { children: React.ReactNode }) {
	const { data: session, isPending } = useSession();
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const pendingQuery = searchParams.get('q');
	const [isRedirecting, setIsRedirecting] = useState(false);

	const isPublicRoute =
		PUBLIC_ROUTES.includes(pathname) || PUBLIC_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));

	const isAuthRedirectRoute = AUTH_REDIRECT_ROUTES.includes(pathname);

	// Reset redirect state when pathname changes (navigation completed)
	useEffect(() => {
		setIsRedirecting(false);
	}, [pathname]);

	useEffect(() => {
		// Don't redirect while loading
		if (isPending) return;

		// If not authenticated and not on a public route, redirect to login
		if (!session && !isPublicRoute) {
			setIsRedirecting(true);
			router.push('/login');
		}

		// If authenticated and on login/signup, redirect to /new (preserving pending query)
		if (session && isAuthRedirectRoute) {
			setIsRedirecting(true);
			router.push(pendingQuery ? `/new?q=${encodeURIComponent(pendingQuery)}` : '/new');
		}
	}, [session, isPending, isPublicRoute, isAuthRedirectRoute, router, pendingQuery]);

	// Only show full-screen loading during redirect transitions
	// This prevents a flash of content before redirect completes
	if (isRedirecting) {
		return (
			<div className='flex min-h-screen items-center justify-center'>
				<Loader2 className='text-primary h-8 w-8 animate-spin' />
			</div>
		);
	}

	// Render children immediately - layout components have their own skeleton states
	// for auth-dependent content (navbar user menu, sidebar conversations, etc.)
	return <>{children}</>;
}
